import { useAPIQuery, useGlobalAPIQueryContext } from "@dev2_techwalnut/tw-api-query-hook";
import { useMemo, useState } from "react";
import Select from "react-select";
import secureLocalStorage from "react-secure-storage";
import Swal from "sweetalert2";

const unwrap = (response) =>
    response?.data?.results?.data ?? response?.data?.data ?? response?.data;

const unwrapList = (payload) => {
    if (!payload) return [];
    const rows =
        payload?.results?.data ??
        payload?.data ??
        (Array.isArray(payload) ? payload : []);
    return Array.isArray(rows) ? rows : [];
};

const formatTs = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
};

const statusLabel = (status) => {
    if (status === "draft") return "Draft";
    if (status === "started") return "In progress";
    if (status === "ended") return "Ended (not compared)";
    if (status === "compared") return "Compared";
    return status || "—";
};

const resultLabel = (row) => {
    if (row.elorca_raw_cycles == null) return "Not fetched";
    return row.passed ? "Pass" : "Fail";
};

const TestCases = () => {
    const { apiClient } = useGlobalAPIQueryContext();
    const privileges = secureLocalStorage.getItem("privileges") || [];
    const canFetchElorca = privileges.includes("test_case_fetch_elorca");
    const [section, setSection] = useState("list");
    const [active, setActive] = useState(null);
    const [machineOption, setMachineOption] = useState(null);
    const [tolerance, setTolerance] = useState("5");
    const [startCount, setStartCount] = useState("");
    const [checkpoint1Count, setCheckpoint1Count] = useState("");
    const [checkpoint2Count, setCheckpoint2Count] = useState("");
    const [endCount, setEndCount] = useState("");
    const [busy, setBusy] = useState(false);

    const { useFetch: useFetchMachines } = useAPIQuery({
        key: "machines",
        endpoint: "/api/machine-list/",
    });
    const { useFetch: useFetchTests } = useAPIQuery({
        key: "api/test-cases",
        endpoint: "/api/test-cases/",
    });

    const { data: machineResponse } = useFetchMachines({
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 3,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });
    const { data: testsResponse, refetch } = useFetchTests({
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 0,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
    });

    const machines = useMemo(() => {
        const rows = unwrapList(machineResponse);
        return rows.map((row) => ({
            value: row.id,
            label: row.machine_name || `Machine ${row.id}`,
        }));
    }, [machineResponse]);

    const tests = useMemo(() => unwrapList(testsResponse), [testsResponse]);

    const errorMessage = (error) =>
        error?.response?.data?.results?.message ||
        error?.response?.data?.message ||
        error.message ||
        "Request failed";

    const openTest = (row) => {
        setActive(row);
        setStartCount(row?.start_cycle_count != null ? String(row.start_cycle_count) : "");
        setCheckpoint1Count(row?.checkpoint_1_count != null ? String(row.checkpoint_1_count) : "");
        setCheckpoint2Count(row?.checkpoint_2_count != null ? String(row.checkpoint_2_count) : "");
        setEndCount(row?.end_cycle_count != null ? String(row.end_cycle_count) : "");
        setSection("run");
    };

    const startNewTestForm = () => {
        setActive(null);
        setStartCount("");
        setCheckpoint1Count("");
        setCheckpoint2Count("");
        setEndCount("");
        setSection("run");
    };

    const applyCase = (payload) => {
        setActive(payload);
        if (payload?.start_cycle_count != null) {
            setStartCount(String(payload.start_cycle_count));
        }
        if (payload?.checkpoint_1_count != null) {
            setCheckpoint1Count(String(payload.checkpoint_1_count));
        }
        if (payload?.checkpoint_2_count != null) {
            setCheckpoint2Count(String(payload.checkpoint_2_count));
        }
        if (payload?.end_cycle_count != null) {
            setEndCount(String(payload.end_cycle_count));
        }
        refetch();
    };

    const postAction = async (url, body, successTitle) => {
        setBusy(true);
        try {
            const response = await apiClient.post(url, body || {});
            const payload = unwrap(response);
            applyCase(payload);
            if (successTitle) {
                Swal.fire({
                    icon: "success",
                    title: successTitle,
                    timer: 1400,
                    showConfirmButton: false,
                });
            }
        } catch (error) {
            Swal.fire("Error", errorMessage(error), "error");
        } finally {
            setBusy(false);
        }
    };

    const createTest = async () => {
        if (!machineOption) {
            Swal.fire("Machine required", "Select a machine to start a test.", "warning");
            return;
        }
        if (tolerance === "" || Number.isNaN(Number(tolerance))) {
            Swal.fire("Tolerance required", "Enter a numeric tolerance percent.", "warning");
            return;
        }
        await postAction(
            "/api/test-cases/",
            {
                machine_id: machineOption.value,
                tolerance_percent: Number(tolerance),
            },
            "Test saved"
        );
        setStartCount("");
        setCheckpoint1Count("");
        setCheckpoint2Count("");
        setEndCount("");
        setSection("run");
    };

    const startTest = async () => {
        if (!active?.id) return;
        if (startCount === "" || Number.isNaN(Number(startCount))) {
            Swal.fire("Start count required", "Enter the starting cycle count.", "warning");
            return;
        }
        await postAction(
            `/api/test-cases/${active.id}/start/`,
            { start_cycle_count: Number(startCount) },
            "Test started"
        );
    };

    const recordCheckpoint = async (slot) => {
        if (!active?.id) return;
        const countValue = slot === 1 ? checkpoint1Count : checkpoint2Count;
        if (countValue === "" || Number.isNaN(Number(countValue))) {
            Swal.fire(
                "Checkpoint count required",
                `Enter the cycle count for checkpoint ${slot}.`,
                "warning"
            );
            return;
        }
        await postAction(
            `/api/test-cases/${active.id}/checkpoint/`,
            { slot, cycle_count: Number(countValue) },
            `Checkpoint ${slot} recorded`
        );
    };

    const endTest = async () => {
        if (!active?.id) return;
        if (endCount === "" || Number.isNaN(Number(endCount))) {
            Swal.fire("End count required", "Enter the ending cycle count.", "warning");
            return;
        }
        await postAction(
            `/api/test-cases/${active.id}/end/`,
            { end_cycle_count: Number(endCount) },
            "Test saved"
        );
    };

    const fetchElorca = async () => {
        if (!active?.id) return;
        await postAction(
            `/api/test-cases/${active.id}/fetch-elorca/`,
            {},
            "Elorca comparison complete"
        );
    };

    const status = active?.case_status;
    const canEditRun = status === "draft" || status === "started";

    return (
        <>
            <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                    <div className="nk-block-head-content">
                        <h4 className="nk-block-title">Test Cases</h4>
                        <p className="text-soft mb-0">
                            Record start, checkpoints, and end. Tests are saved without fetching Elorca.
                            Fetching Elorca is optional and does not update daily plans or production data.
                        </p>
                    </div>
                    {section === "list" ? (
                        <div className="nk-block-head-content">
                            <button type="button" className="btn btn-primary" onClick={startNewTestForm}>
                                New test
                            </button>
                        </div>
                    ) : (
                        <div className="nk-block-head-content">
                            <button
                                type="button"
                                className="btn btn-outline-light"
                                onClick={() => {
                                    refetch();
                                    setSection("list");
                                }}
                            >
                                All tests
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <ul className="nav nav-tabs mb-3">
                <li className="nav-item">
                    <button
                        type="button"
                        className={`nav-link ${section === "list" ? "active" : ""}`}
                        onClick={() => {
                            refetch();
                            setSection("list");
                        }}
                    >
                        All tests
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        type="button"
                        className={`nav-link ${section === "run" ? "active" : ""}`}
                        onClick={() => setSection("run")}
                    >
                        Run test
                    </button>
                </li>
            </ul>

            {section === "list" ? (
                <div className="card card-bordered">
                    <div className="card-inner">
                        <div className="table-responsive">
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Machine</th>
                                        <th>Status</th>
                                        <th>Started</th>
                                        <th>Start count</th>
                                        <th>Checkpoint 1</th>
                                        <th>CP1 count</th>
                                        <th>Checkpoint 2</th>
                                        <th>CP2 count</th>
                                        <th>Ended</th>
                                        <th>End count</th>
                                        <th>Manual Δ</th>
                                        <th>Elorca raw</th>
                                        <th>Mould × count</th>
                                        <th>Diff %</th>
                                        <th>Result</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tests.length === 0 ? (
                                        <tr>
                                            <td colSpan="16">No tests yet.</td>
                                        </tr>
                                    ) : (
                                        tests.map((row) => (
                                            <tr
                                                key={row.id}
                                                style={{ cursor: "pointer" }}
                                                onClick={() => openTest(row)}
                                            >
                                                <td>{row.id}</td>
                                                <td>{row.machine_name}</td>
                                                <td>{statusLabel(row.case_status)}</td>
                                                <td>{formatTs(row.started_at)}</td>
                                                <td>{row.start_cycle_count ?? "—"}</td>
                                                <td>{formatTs(row.checkpoint_1_at)}</td>
                                                <td>{row.checkpoint_1_count ?? "—"}</td>
                                                <td>{formatTs(row.checkpoint_2_at)}</td>
                                                <td>{row.checkpoint_2_count ?? "—"}</td>
                                                <td>{formatTs(row.ended_at)}</td>
                                                <td>{row.end_cycle_count ?? "—"}</td>
                                                <td>{row.manual_delta ?? "—"}</td>
                                                <td>{row.elorca_raw_cycles ?? "—"}</td>
                                                <td>{row.elorca_mould_quantity ?? "—"}</td>
                                                <td>{row.percent_diff ?? "—"}</td>
                                                <td>{resultLabel(row)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="alert alert-warning">
                        Pass/fail uses raw cycles only after an Elorca fetch; mould-multiplied count is for display.
                        Ending a test saves it even if Elorca is never fetched.
                    </div>

                    <div className="card card-bordered mb-4">
                        <div className="card-inner">
                            <h6 className="title">New test</h6>
                            <div className="row g-3 align-items-end">
                                <div className="col-md-5">
                                    <label className="form-label">Machine</label>
                                    <Select
                                        options={machines}
                                        value={machineOption}
                                        onChange={setMachineOption}
                                        placeholder="Select machine"
                                        isDisabled={busy || canEditRun}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Tolerance %</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.1"
                                        className="form-control"
                                        value={tolerance}
                                        onChange={(event) => setTolerance(event.target.value)}
                                        disabled={busy || canEditRun}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={createTest}
                                        disabled={busy || canEditRun}
                                    >
                                        Create test
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {active ? (
                        <div className="card card-bordered">
                            <div className="card-inner">
                                <h6 className="title">
                                    Test #{active.id} · {active.machine_name} · {statusLabel(active.case_status)}
                                </h6>
                                <div className="row g-3">
                                    <div className="col-md-4">
                                        <label className="form-label">Start cycle count</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={startCount}
                                            onChange={(event) => setStartCount(event.target.value)}
                                            disabled={busy || status !== "draft"}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-primary mt-2"
                                            onClick={startTest}
                                            disabled={busy || status !== "draft"}
                                        >
                                            Start test
                                        </button>
                                        <div className="text-soft mt-1">{formatTs(active.started_at)}</div>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Checkpoints (count + timestamp)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            placeholder="Checkpoint 1 count"
                                            value={checkpoint1Count}
                                            onChange={(event) => setCheckpoint1Count(event.target.value)}
                                            disabled={
                                                busy || status !== "started" || Boolean(active.checkpoint_1_at)
                                            }
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary mt-2"
                                            onClick={() => recordCheckpoint(1)}
                                            disabled={
                                                busy || status !== "started" || Boolean(active.checkpoint_1_at)
                                            }
                                        >
                                            Record checkpoint 1
                                        </button>
                                        <div className="text-soft mt-1">
                                            {formatTs(active.checkpoint_1_at)}
                                            {active.checkpoint_1_count != null
                                                ? ` · count ${active.checkpoint_1_count}`
                                                : ""}
                                        </div>
                                        <input
                                            type="number"
                                            className="form-control mt-3"
                                            placeholder="Checkpoint 2 count"
                                            value={checkpoint2Count}
                                            onChange={(event) => setCheckpoint2Count(event.target.value)}
                                            disabled={
                                                busy ||
                                                status !== "started" ||
                                                !active.checkpoint_1_at ||
                                                Boolean(active.checkpoint_2_at)
                                            }
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary mt-2"
                                            onClick={() => recordCheckpoint(2)}
                                            disabled={
                                                busy ||
                                                status !== "started" ||
                                                !active.checkpoint_1_at ||
                                                Boolean(active.checkpoint_2_at)
                                            }
                                        >
                                            Record checkpoint 2
                                        </button>
                                        <div className="text-soft mt-1">
                                            {formatTs(active.checkpoint_2_at)}
                                            {active.checkpoint_2_count != null
                                                ? ` · count ${active.checkpoint_2_count}`
                                                : ""}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">End cycle count</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={endCount}
                                            onChange={(event) => setEndCount(event.target.value)}
                                            disabled={busy || status !== "started"}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-primary mt-2"
                                            onClick={endTest}
                                            disabled={busy || status !== "started"}
                                        >
                                            End and save
                                        </button>
                                        <div className="text-soft mt-1">{formatTs(active.ended_at)}</div>
                                    </div>
                                </div>
                                {canFetchElorca ? (
                                    <div className="mt-3">
                                        <button
                                            type="button"
                                            className="btn btn-success"
                                            onClick={fetchElorca}
                                            disabled={busy || (status !== "ended" && status !== "compared")}
                                        >
                                            Fetch Elorca
                                        </button>
                                        <span className="text-soft ms-2">Optional. The test is already saved.</span>
                                    </div>
                                ) : null}
                                {active.elorca_raw_cycles != null ? (
                                    <div className="mt-4">
                                        <h6>Elorca comparison</h6>
                                        <ul className="list-unstyled">
                                            <li>Manual delta: {active.manual_delta}</li>
                                            <li>Elorca raw cycles: {active.elorca_raw_cycles}</li>
                                            <li>
                                                Mould × cycles ({active.mould_size_snapshot} × {active.elorca_raw_cycles}):{" "}
                                                {active.elorca_mould_quantity}
                                            </li>
                                            <li>Difference: {active.percent_diff}%</li>
                                            <li>
                                                Result:{" "}
                                                <strong className={active.passed ? "text-success" : "text-danger"}>
                                                    {active.passed ? "Pass" : "Fail"}
                                                </strong>
                                            </li>
                                        </ul>
                                    </div>
                                ) : (
                                    <p className="text-soft mt-3 mb-0">
                                        Elorca has not been fetched for this test. Manual times and counts are stored.
                                    </p>
                                )}
                            </div>
                        </div>
                    ) : (
                        <p className="text-soft">Create a test above, or open one from All tests.</p>
                    )}
                </>
            )}
        </>
    );
};

export default TestCases;
